import { useEffect, useMemo, useState } from 'react'
import { FocusLayout, ParticipantTile, TrackReferenceOrPlaceholder } from '@livekit/components-react'
import { Participant, Room } from 'livekit-client'
import useMuseTracks from './useMuseTracks'
import { logger } from '@/services/Logger'
import { cn } from '@/utlis/cn'
import './speaker-layout.css'

const log = logger.createChildLogger({ name: 'egress-speaker-layout' })

type SpeakerLayoutProps = {
    room: Room
}

export function SpeakerLayout(props: SpeakerLayoutProps) {
    const museTracks = useMuseTracks(props.room)

    const [speaker, setSpeaker] = useState<Participant | null>(null)

    useEffect(() => {
        for (const s of props.room.activeSpeakers) {
            if (s.sid !== speaker?.sid) {
                setSpeaker(s)
            }
        }
    }, [props.room.activeSpeakers])

    const focusTrack = useMemo(() => {
        if (museTracks.screen) {
            return museTracks.screen
        }

        if (speaker) {
            return (
                museTracks.all.find(
                    (t) => t.participant.sid === speaker.sid && t.publication?.source !== 'screen_share'
                ) ?? null
            )
        }

        return museTracks.all[0] ?? null
    }, [museTracks, speaker])

    const speakerTrack = useMemo(() => {
        return museTracks.all.find(
            (t) => t.participant.sid === speaker?.sid && t.publication?.source !== 'screen_share'
        )
    }, [speaker, museTracks.all])

    const presenting = !!museTracks.screen

    return (
        <div className="w-screen h-screen overflow-hidden relative">
            {focusTrack && <FocusLayout trackRef={focusTrack} className="bg-neutral-800 w-full h-full" />}
            <div className="pip absolute w-[300px] top-4 right-4 grid grid-cols-1 gap-4">
                {presenting && <Tile track={speakerTrack} />}
            </div>
        </div>
    )
}

type TileProps = {
    track?: TrackReferenceOrPlaceholder
    speaking?: boolean
}
function Tile(props: TileProps) {
    if (!props.track) {
        return null
    }

    return (
        <div className="relative">
            <ParticipantTile trackRef={props.track} className="rounded-[24px]" />
            <div className={cn('absolute inset-0', props.speaking && 'border-2 border-muse-yellow')}></div>
        </div>
    )
}
